import { useEffect } from 'react'

import Intercom, {
  show,
  showNewMessage,
  startTour,
  update,
} from '@intercom/messenger-js-sdk'
import { isNil, omitBy } from 'lodash'

import config from '@/config/index'
import { useProject } from '@/context/projectContext'
import { useUser } from '@/context/userContext'
import { formatRoleName } from '@/modules/adminUsers/roles'
import { createCommaSeparatedString } from '@/utils/createCommaSeparated'
import { useRoles } from '@/utils/ensureRole'
import { getUserName } from '@/utils/getName'

type Props = {
  anonymous?: boolean
}

export const LiveChat: React.FC<Props> = ({ anonymous }) => {
  const { user, builder, adminUser } = useUser() || {}
  const { project } = useProject() || {}
  const { roles } = useRoles()
  const isE2E = user?.email === config.e2eEmail

  useEffect(() => {
    if (isE2E || anonymous) return

    Intercom({
      api_base: 'https://api-iam.intercom.io',
      app_id: 'cli6vnet',
      horizontal_padding: 25,
      vertical_padding: 10,
      phone: `${adminUser?.mobile}`,
      email: user?.email,
      'BuildPass User Type': 'admin',
      user_id: user?.id,
      company: {
        id: builder?.id,
        name: builder?.company?.name,
        plan: builder?.subscription,
        tier: builder?.subscription,
        subscript_id: builder?.subscriptId,
      },
    })
  }, [
    isE2E,
    anonymous,
    adminUser?.modules,
    builder?.company?.name,
    builder?.id,
    builder?.subscription,
    adminUser?.mobile,
    builder?.subscriptId,
    user?.email,
    user?.id,
  ])

  useEffect(() => {
    if (isE2E || anonymous || !user) return

    update(
      omitBy(
        {
          name: getUserName(user),
          email: user.email,
          company: user.adminUser?.builder?.company?.name,
          job_title:
            roles?.length > 0
              ? createCommaSeparatedString(roles?.map(formatRoleName))
              : null,
          created_at: new Date(user.createdAt).getTime() / 1000,
          app_host: config.appHost,
          currentProjectId: project?.id,
        },
        isNil
      )
    )
  }, [isE2E, anonymous, roles, user, project?.id])

  return null
}

export const openLiveChat = () => {
  show()
}

export const openLiveChatToNewConversation = () => {
  show()
  showNewMessage('')
}

export const openLiveChatToAnswers = () => {
  show()
  startTour('help-center')
}
